import {
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import { useFields } from './useFields';

// State ids are stored as 'US-IL'; a bare 'IL' means the same state. Reduced to
// the bare code so either spelling compares equal.
const normalizeStateCode = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/^US-/, '');

const CreateNewCentralizedReportForm = forwardRef(
  (
    {
      organizationId,
      configuration,
      onPropertyOptionsChange,
      userProperties = [],
      userPropertyGroups = [],
      onSubmit,
      onValidationChange,
    },
    ref,
  ) => {
    // Transform user properties to options format
    const propertyOptions = useMemo(() => {
      if (!userProperties || !Array.isArray(userProperties)) {
        return [];
      }

      // Filter only active properties
      const activeProperties = userProperties.filter(
        (prop) => prop?.propertyStatus?.description !== 'Inactive',
      );

      // A report may restrict which properties it applies to. Only `states` is
      // supported today; a report without the config offers every property, so
      // existing reports are unaffected.
      //
      // Both sides are State ids ('US-IL') today, so they already match. They
      // are compared on a normalized form anyway: an eligibility list written
      // with bare state codes would otherwise match nothing and leave the
      // Properties field empty, which reads as the report being broken rather
      // than as a typo in its configuration.
      const eligibleStates = configuration?.propertyEligibility?.states;
      const eligibleProperties =
        Array.isArray(eligibleStates) && eligibleStates.length > 0
          ? activeProperties.filter((prop) =>
              eligibleStates
                .map(normalizeStateCode)
                .includes(normalizeStateCode(prop?.physicalState)),
            )
          : activeProperties;

      return eligibleProperties.map((prop) => ({
        text: prop.name || prop.id,
        value: prop.id,
        disabled: false,
      }));
    }, [userProperties, configuration]);

    // Transform user property groups to options format. For a report limited to
    // some properties (a state, or the server's eligibility list), a group with
    // none of the offered properties is left out: picking it would select
    // nothing. Picking a mixed group already selects only its offered members.
    const restrictsProperties =
      configuration?.eligibilityFilter === true ||
      configuration?.propertyEligibility?.states?.length > 0;
    const propertyGroupOptions = useMemo(() => {
      if (!userPropertyGroups || !Array.isArray(userPropertyGroups)) {
        return [];
      }

      const offeredPropertyIds = new Set(
        propertyOptions.map((option) => option.value),
      );

      return userPropertyGroups
        .filter(
          (group) =>
            !restrictsProperties ||
            (group.propertyIds ?? []).some((id) => offeredPropertyIds.has(id)),
        )
        .map((group) => ({
          text: group.name || group.id,
          value: group.id,
          disabled: false,
        }));
    }, [userPropertyGroups, propertyOptions, restrictsProperties]);

    useEffect(() => {
      if (onPropertyOptionsChange && propertyOptions.length > 0) {
        onPropertyOptionsChange(propertyOptions);
      }
    }, [propertyOptions, onPropertyOptionsChange]);

    // Get default timeSelection from configuration
    const defaultTimeSelection = useMemo(() => {
      if (
        configuration?.timeSelectionOptions &&
        configuration.timeSelectionOptions.length > 0
      ) {
        return configuration.timeSelectionOptions[0].toLowerCase();
      }
      return '';
    }, [configuration?.timeSelectionOptions]);

    const isSettingPropertiesRef = useRef(false);

    const ReactHookFormProps = useReactHookFormProps({
      name: 'createCentralizedReportForm',
      defaultValues: {
        timeSelection: defaultTimeSelection,
        dateFrom: null,
        dateTo: null,
        date: null,
        year: '',
        propertyGroup: '',
        properties: [],
        format: '',
        exportAs: 'individual',
        ...configuration?.defaultValues,
      },
      mode: 'onChange',
    });

    const selectedPropertyGroupId = ReactHookFormProps?.watch
      ? ReactHookFormProps.watch('propertyGroup')
      : null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedProperties = ReactHookFormProps?.watch
      ? ReactHookFormProps.watch('properties')
      : [];

    const setValue = ReactHookFormProps?.setValue;
    const trigger = ReactHookFormProps?.trigger;
    const getValues = ReactHookFormProps?.getValues;
    const selectedDateFrom = ReactHookFormProps?.watch
      ? ReactHookFormProps.watch('dateFrom')
      : null;

    // The End Date is checked against the Start Date, and a field's rules only
    // run when that field changes, so a Start Date changed after the End Date
    // re-checks it. Only once an End Date is entered: an empty one is left to its
    // required rule until the user gets to it.
    useEffect(() => {
      if (!trigger || !getValues || !getValues()?.dateTo) return;
      trigger('dateTo');
    }, [selectedDateFrom, trigger, getValues]);

    const fields = useFields({
      configuration,
      propertyOptions,
      propertyGroupOptions,
      userPropertyGroups,
      selectedProperties,
    });

    useEffect(() => {
      if (!selectedPropertyGroupId || !setValue) return;

      const selectedGroup = userPropertyGroups.find(
        (group) => group.id === selectedPropertyGroupId,
      );

      if (selectedGroup && selectedGroup.propertyIds) {
        const availablePropertyIds = propertyOptions.map((opt) => opt.value);

        const propertyIdsToSelect = selectedGroup.propertyIds.filter((id) =>
          availablePropertyIds.includes(id),
        );

        isSettingPropertiesRef.current = true;
        // Validated so a "required" error left from an empty selection clears.
        setValue('properties', propertyIdsToSelect, { shouldValidate: true });
        // Defer the reset to the next microtask so the flag is lowered only
        // after React Hook Form finishes applying the setValue change. This
        // avoids the timing issues we hit with setTimeout-based guards.
        Promise.resolve().then(() => {
          isSettingPropertiesRef.current = false;
        });
      }
    }, [
      selectedPropertyGroupId,
      userPropertyGroups,
      propertyOptions,
      setValue,
    ]);

    useEffect(() => {
      if (!selectedPropertyGroupId || !setValue || !selectedProperties) return;

      if (isSettingPropertiesRef.current) return;

      const selectedGroup = userPropertyGroups.find(
        (group) => group.id === selectedPropertyGroupId,
      );

      if (selectedGroup && selectedGroup.propertyIds) {
        const availablePropertyIds = propertyOptions.map((opt) => opt.value);
        const groupPropertyIds = selectedGroup.propertyIds.filter((id) =>
          availablePropertyIds.includes(id),
        );

        if (selectedProperties.length === 0) return;

        const allGroupPropertiesSelected = groupPropertyIds.every((id) =>
          selectedProperties.includes(id),
        );

        if (!allGroupPropertiesSelected && selectedProperties.length > 0) {
          setValue('propertyGroup', '');
        }
      }
    }, [
      selectedProperties,
      selectedPropertyGroupId,
      userPropertyGroups,
      propertyOptions,
      setValue,
    ]);

    const handleFormSubmit = useCallback(
      (formValues) => {
        if (onSubmit) {
          onSubmit(formValues);
        }
      },
      [onSubmit],
    );

    // Expose submit method via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (ReactHookFormProps?.handleSubmit) {
            ReactHookFormProps.handleSubmit(handleFormSubmit)();
          }
        },
      }),
      [ReactHookFormProps, handleFormSubmit],
    );

    useEffect(() => {
      if (onValidationChange && ReactHookFormProps) {
        onValidationChange(ReactHookFormProps.isValid);
      }
    }, [onValidationChange, ReactHookFormProps, ReactHookFormProps?.isValid]);

    return (
      <Form
        fields={fields}
        ReactHookFormProps={ReactHookFormProps}
        onSubmit={handleFormSubmit}
        hideSubmitButton
        containerSX={{
          textAlign: 'left',
          width: '100%',
          maxWidth: '100%',
        }}
      />
    );
  },
);

CreateNewCentralizedReportForm.displayName = 'CreateNewCentralizedReportForm';

export default CreateNewCentralizedReportForm;
